import { isDefined } from '@sama/js-core';
import { memoize } from 'lodash';
import { Sprite, SpriteMaterial, TextureLoader } from 'three';
import {
  CUBE_SIZE,
  CuboidLocationState3D,
} from '../../../state/cuboid-location-state-3d';
import { getFocusedShapeIndex } from '../../../state/focused-shape-utils';
import { PointCloudContentState } from '../../../state/point-cloud-content-state';
import { mapPoint, multiplyPoints } from '../../../utils/point-utils';
import { RendererLayer, setRendererLayers } from '../../render-utils';
import {
  ConfigureMeshesConfiguration,
  RenderConfiguration,
} from '../abstract-mesh-helper';
import { highlightColor } from '../color-utils';
import { pixelToOrthographicWorldSize } from '../mesh-utils';
import { CuboidFace } from './cuboid-helper';

export const selectableFoiColor = '#807F7C';
export const hoveredFoiColor = '#FFFFFF';

export class CuboidFoiSelector extends Sprite {
  constructor(private attachedFace: CuboidFace) {
    super();
    this.userData = { meshType: 'foi-selector' };
    this.name = `${attachedFace}`;
    this.frustumCulled = false;
    this.center.set(0.5, 0.1); // base of the foi texture
  }

  dispose() {
    this.geometry.dispose();
  }

  // eslint-disable-next-line complexity -- complexity is going to be reduced when new-edit-panel FF is promoted
  updateRendererLayers(
    state: PointCloudContentState,
    renderConfig: RenderConfiguration,
    shapeIndex: number,
  ) {
    setRendererLayers([], this);
    if (!renderConfig.isUsingNewEditPanels) {
      return;
    }

    const isFocused = getFocusedShapeIndex(state) === shapeIndex;
    const isEditingInPanels = isDefined(state.render.currentEditSource?.index);
    if (!isFocused || isEditingInPanels) {
      return;
    }

    const layers = this.getFaceLayers();
    const isCurrentFoi = this.attachedFace === CuboidFace.Front;
    if (isCurrentFoi) {
      setRendererLayers(layers, this);
      return;
    }

    if (state.render.editMode !== 'select face') {
      return;
    }

    setRendererLayers(layers, this);
  }

  update(
    config: ConfigureMeshesConfiguration,
    location: CuboidLocationState3D,
  ) {
    const { cameraLayer, state } = config;
    const shouldBeRendered = this.getFaceLayers().includes(cameraLayer);
    if (!shouldBeRendered) {
      return;
    }

    this.updateScale(config, location);
    this.updatePosition(config, location);
    this.updateStyle(cameraLayer, state);
  }

  private updatePosition(
    config: ConfigureMeshesConfiguration,
    { scale }: CuboidLocationState3D,
  ) {
    this.position.set(0, 0, 0);

    let translationAxis = 0;
    let translationDirection = 1;
    if (this.attachedFace === CuboidFace.Front) {
      translationAxis = 1;
      translationDirection = -1;
    } else if (this.attachedFace === CuboidFace.Right) {
      translationAxis = 0;
      translationDirection = -1;
    } else if (this.attachedFace === CuboidFace.Back) {
      translationAxis = 1;
      translationDirection = 1;
    }

    const gapPx = 10;
    const outwardsOffset = pixelToOrthographicWorldSize(gapPx, config);
    const scaleCompensatedOffset = outwardsOffset / scale[translationAxis];
    const translation =
      translationDirection * (scaleCompensatedOffset + CUBE_SIZE / 2);

    this.position.setComponent(translationAxis, translation);
  }

  private updateScale(
    config: ConfigureMeshesConfiguration,
    { scale }: CuboidLocationState3D,
  ) {
    const foiWidthPx = 28;
    const foiScale = pixelToOrthographicWorldSize(foiWidthPx, config);
    const compensatedScale = multiplyPoints(
      [foiScale, foiScale, 1],
      mapPoint(scale, (component) => 1 / component),
    );
    this.scale.fromArray(compensatedScale);
  }

  private updateStyle(
    rendererLayer: RendererLayer,
    state: PointCloudContentState,
  ) {
    let color = selectableFoiColor;
    if (this.attachedFace === CuboidFace.Front) {
      color = highlightColor;
    } else if (state.editPanel.highlightedFoiSelector === this.attachedFace) {
      color = hoveredFoiColor;
    }

    this.material = getFoiSelectorMaterial(
      color,
      this.attachedFace,
      rendererLayer,
    );
  }

  private getFaceLayers() {
    let layers: RendererLayer[] = [];

    if ([CuboidFace.Left, CuboidFace.Right].includes(this.attachedFace)) {
      layers = [RendererLayer.SideViewTop, RendererLayer.SideViewMiddle];
    }
    if ([CuboidFace.Back, CuboidFace.Front].includes(this.attachedFace)) {
      layers = [RendererLayer.SideViewTop, RendererLayer.SideViewBottom];
    }

    return layers;
  }
}

// We memoize so material compilation and texture binding aren't triggered
// multiple times
// We don't use the MaterialService because SpriteMaterial is not an H3Material
const getFoiSelectorMaterial = memoize(
  (color: string, cuboidFace: CuboidFace, rendererLayer: RendererLayer) =>
    new SpriteMaterial({
      color,
      depthTest: false, // be on top of points
      map: foiSelectorTexture,
      rotation: getMaterialRotation(cuboidFace, rendererLayer),
    }),
  (color: string, cuboidFace: CuboidFace, rendererLayer: RendererLayer) =>
    // cache returns based on all arguments
    JSON.stringify({ color, cuboidFace, rendererLayer }),
);

// eslint-disable-next-line complexity -- Ok for shallow switches
function getMaterialRotation(cuboidFace: CuboidFace, layer: RendererLayer) {
  switch (cuboidFace) {
    case CuboidFace.Right:
      return -Math.PI / 2;
    case CuboidFace.Left:
      return Math.PI / 2;
    case CuboidFace.Back:
      return layer === RendererLayer.SideViewTop ? Math.PI : Math.PI / 2;
    case CuboidFace.Front:
      return layer === RendererLayer.SideViewTop ? 0 : -Math.PI / 2;
    default:
      return 0;
  }
}

const foiSelectorTexture = buildFoiSelectorTexture();

function buildFoiSelectorTexture() {
  const triangleSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="512" height="512">
      <path fill="white" d="m6.76,414.43l221.24,-352.23c12.88,-20.5 42.9,-20.5 55.78,0l221.24,352.23c13.68,21.77 -2.06,50 -27.89,50l-442.48,0c-25.81,0 -41.55,-28.23 -27.89,-50z"/>
    </svg>
  `;

  const svgBlob = new Blob([triangleSvg], { type: 'image/svg+xml' });
  const svgUrl = URL.createObjectURL(svgBlob);
  const textureLoader = new TextureLoader();

  return textureLoader.load(svgUrl);
}
